import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";
import { Avatar } from "../Atoms";
import * as api from "../../api";
import { clicable } from "../ClicableTeclado.jsx";
import { nombreCorto } from "../../utils";

// Foto de Odoo, con las iniciales como respaldo mientras carga o si no tiene.
//
// No pide nada por su cuenta: lee de la cache que rellena precargarAvatares en
// un solo viaje. Antes cada avatar era una peticion y bastaba un desplegable de
// 40 personas para agotar el limitador de la API.
export function AvatarOdoo({ modelo, id, nombre, size = 22 }) {
  const url = id ? api.avatarCacheado(modelo, id) : null;
  // Una foto que el navegador no consigue pintar —el dato de Odoo llega
  // truncado o no es una imagen— dejaba el icono roto en mitad de la ficha.
  // Cuando falla se cae a las iniciales, igual que cuando no hay foto.
  const [roto, setRoto] = useState(false);
  useEffect(() => { setRoto(false); }, [url]);
  if (!url || roto) return <Avatar name={nombre || "?"} size={size}/>;
  return <img src={url} alt={nombre || ""} title={nombre || ""} onError={() => setRoto(true)}
    style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", flexShrink:0 }}/>;
}

// Un <select> nativo no puede pintar imágenes, así que el asignado necesita
// desplegable propio. Lleva buscador porque incluso filtrado por departamento
// la lista puede pasar de diez personas.
//
// Vive fuera del detalle porque las tarjetas del tablero montan el mismo
// desplegable: asignar una incidencia es lo que más se hace y no merece abrir
// la ficha entera.
export default function SelectorUsuario({ usuarios, valor, nombre, disabled, onChange,
                                          compacto = false }) {
  const [abierto, setAbierto] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [, redibujar] = useState(0);
  const [pos, setPos] = useState(null);       // sitio de la lista, en coordenadas de pantalla
  const caja = useRef(null);
  const boton = useRef(null);

  // La lista se pinta en el body y en position:fixed. Dentro de la tarjeta no
  // cabe: las columnas del tablero llevan su propio scroll y un desplegable
  // absoluto se recortaba al borde de la columna.
  const situar = () => {
    const r = boton.current?.getBoundingClientRect();
    if (!r) return;
    const alto = 320;
    const abajo = window.innerHeight - r.bottom > alto || r.top < alto;
    setPos({ left: Math.min(r.left, window.innerWidth - 274),
             ...(abajo ? { top: r.bottom + 5 } : { bottom: window.innerHeight - r.top + 5 }) });
  };

  // Un unico viaje para todas las fotos de la lista, y solo al abrirla.
  useEffect(() => {
    if (!abierto || !usuarios.length) return;
    api.precargarAvatares("res.users", usuarios.map(u => u.id)).then(() => redibujar(n => n + 1));
  }, [abierto, usuarios]);

  // Al desplazar, la lista fija dejaria de acompanar a su boton: se cierra.
  useEffect(() => {
    if (!abierto) return;
    const fuera = e => {
      if (caja.current && !caja.current.contains(e.target) &&
          !e.target.closest?.("[data-lista-usuarios]")) setAbierto(false);
    };
    const cerrar = () => setAbierto(false);
    document.addEventListener("mousedown", fuera);
    window.addEventListener("scroll", cerrar, true);
    window.addEventListener("resize", cerrar);
    return () => {
      document.removeEventListener("mousedown", fuera);
      window.removeEventListener("scroll", cerrar, true);
      window.removeEventListener("resize", cerrar);
    };
  }, [abierto]);

  const visibles = usuarios.filter(u => u.name.toLowerCase().includes(filtro.trim().toLowerCase()));
  const elegir = id => { setAbierto(false); setFiltro(""); onChange(id); };

  return (
    <div ref={caja} style={{ position:"relative", minWidth:0 }}>
      <button ref={boton} className="btn" disabled={disabled}
        onClick={() => { situar(); setAbierto(p => !p); }}
        title={nombre || "Sin asignar"}
        aria-label={nombre ? `Asignado a ${nombre}. Cambiar` : "Sin asignar. Elegir persona"}
        style={{ display:"flex", alignItems:"center", gap: compacto ? 4 : 6,
                 padding: compacto ? "2px 6px 2px 2px" : "4px 9px 4px 4px",
                 borderRadius:20, background: valor ? "var(--surface2)" : "transparent",
                 border: valor ? "1px solid var(--border-ctl)" : "1px dashed var(--border-ctl)",
                 cursor: disabled ? "not-allowed" : "pointer", fontFamily:"var(--font)",
                 fontSize: compacto ? 10 : 11.5, fontWeight:600,
                 maxWidth: compacto ? 148 : 230, minWidth:0,
                 color: valor ? "var(--text)" : "var(--text3)" }}>
        <AvatarOdoo modelo="res.users" id={valor} nombre={nombre || "—"} size={compacto ? 17 : 22}/>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {nombre ? nombreCorto(nombre) : "Sin asignar"}
        </span>
        <Icon name="chevron-down" size={compacto ? 9 : 10} color="var(--text3)"/>
      </button>

      {abierto && pos && createPortal(
        <div data-lista-usuarios
          style={{ position:"fixed", ...pos, zIndex:500, width:262,
                   background:"var(--surface)", border:"1px solid var(--border)", borderRadius:11,
                   boxShadow:"var(--shadow-lg)", overflow:"hidden" }}>
          <input aria-label="Buscar persona" autoFocus value={filtro} onChange={e => setFiltro(e.target.value)}
            placeholder="Buscar persona…"
            style={{ width:"100%", boxSizing:"border-box", padding:"9px 11px", border:"none",
                     borderBottom:"1px solid var(--border2)", background:"var(--surface2)",
                     color:"var(--text)", fontSize:12, fontFamily:"var(--font)", outline:"none" }}/>

          <div style={{ maxHeight:266, overflowY:"auto" }}>
            <div {...clicable(() => elegir(false))}
              style={{ padding:"8px 11px", cursor:"pointer", fontSize:12, color:"var(--text3)" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--bg)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              Sin asignar
            </div>
            {visibles.map(u => (
              <div key={u.id} {...clicable(() => elegir(u.id))}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 11px", cursor:"pointer",
                         background: String(u.id) === String(valor) ? "var(--accent-bg)" : "transparent" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg)"}
                onMouseLeave={e => e.currentTarget.style.background =
                  String(u.id) === String(valor) ? "var(--accent-bg)" : "transparent"}>
                <AvatarOdoo modelo="res.users" id={u.id} nombre={u.name} size={24}/>
                <span style={{ fontSize:12, color:"var(--text)", overflow:"hidden",
                               textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.name}</span>
              </div>
            ))}
            {visibles.length === 0 && (
              <div style={{ padding:"10px 11px", fontSize:11.5, color:"var(--text3)" }}>Nadie coincide.</div>
            )}
          </div>
        </div>,
        document.body)}
    </div>
  );
}
